import { getCookie, setCookie, removeCookie } from 'helpers/VanillaCookie';
import { fetchData } from 'video/shared/smart-tag-adapter/network-tools';
import { isKidsVideo } from 'video/shared/smart-tag-adapter/video-tools';
import { fwGDPRPromise } from '../bundles/livetv/FreeWheelGDPRHandler';

const CACHE_ENABLED = true;

export default class FreeWheelFMS {
    // Registered in videoTop.js and Instantiated in video_player.tpl.php
    constructor(params) {
        // Feature flag for FreeWheel vs. other ad ecosystems
        this.enabled = params.freeWheelAds !== undefined ? params.freeWheelAds : false;
        // e.g. "cbs" vs. "pplus"
        this.brand = params.partner !== undefined ? params.partner : 'undefined';
        this.endPoint = '/freewheel/fms.json';
        // re: COPPA
        this.profileTypeKids = 'KIDS';
        this.dai = params.dai;
        this.metaData = {};
        this.coppa = 0;
        this.uuidHandler = null;
        this.videoAds = {
            enabled: false,
        };

        this.configureDebug('FMS', 'debugFMS');
        this.debug('FreeWheel ' + (this.enabled ? 'enabled' : 'disabled'));

        if (this.enabled) {
            this.debug('Caching ', CACHE_ENABLED ? 'enabled' : 'DISABLED');
            this.debug('DAI = ', this.dai ? 'true' : 'false');
        }
    }

    setMetaData(metaData) {
        this.metaData = metaData;
        this.configureMetaDataParams();
        return this;
    }

    setVideoAdsParameters(videoAds) {
        this.videoAds = videoAds;
        return this;
    }

    setUUIDHandler(handler) {
        this.uuidHandler = handler;
        return this;
    }

    configureMetaDataParams() {
        this.coppa = isKidsVideo(this.metaData.videoProperties);
    }

    // get/configure FMS payload: use CBS.Registry for consumers, e.g. smart tag adaptor
    configure() {
        // Is FreeWheel enabled?
        if (this.enabled) {
            // promise for consumers
            CBS.Registry.fmsPromise = new Promise(async (resolve, reject) => {
                let key = this.cacheKey();

                // 1) check cache
                let item = this.cacheGet(key);
                if (item && !this.coppa) {
                    this.debug('Using data from cache', item);
                    // value for consumers
                    CBS.Registry.fms = item.data.fms_params;
                    if (this.videoAds.enabled) {
                        GDPRData = await fwGDPRPromise;
                        this.setGDPRData(GDPRData);
                    }
                    resolve();
                    return;
                }

                let GDPRData;

                if (this.videoAds.enabled) {
                    GDPRData = await fwGDPRPromise;
                }

                // 2) fetch from server API
                this.debug('Fetching from server API');
                let urlSearchParams = new URLSearchParams();
                urlSearchParams.append('dai', this.dai ? 1 : 0);
                urlSearchParams.append('coppa', this.coppa ? 1 : 0);
                urlSearchParams.append('userid', this.getUserID());
                if (GDPRData) {
                    GDPRData.gdpr_consent !== undefined &&
                        urlSearchParams.append('gdpr_consent', GDPRData.gdpr_consent);
                    GDPRData.gdpr !== undefined && urlSearchParams.append('gdpr', GDPRData.gdpr);
                }
                let url = this.endPoint + '?' + urlSearchParams.toString();
                let response;
                try {
                    response = await fetchData(url);
                } catch (error) {
                    this.debug('Fetch error', error);
                    reject();
                }
                if (!response?.ok) {
                    throw Error(response.statusText);
                }
                try {
                    const itemResponse = await response.json();
                    if (itemResponse.success) {
                        // value for consumers
                        this.debug('Data from server API:', item);
                        CBS.Registry.fms = itemResponse.data.fms_params;
                        this.setGDPRData(GDPRData);
                        if (!this.coppa) {
                            this.cacheSet(itemResponse, key, itemResponse.data.fms_id_ttl);
                        }
                        resolve();
                    } else {
                        this.debug('Fetch error', itemResponse);
                        CBS.Registry.fms = itemResponse.data.fallback;
                        resolve();
                    }
                } catch (error) {
                    this.debug('Fetch error', error);
                    reject();
                }
            });
        }
    }

    setGDPRData(GDPRData) {
        if (GDPRData) {
            window.CBS.Registry.GDPR = {
                _fw_gdpr_consent: GDPRData.gdpr_consent,
                _fw_gdpr: GDPRData.gdpr,
            };
        }
    }

    // simple expiring cache based on browser localstorage

    // generate 4 part cache key
    cacheKey() {
        let id = CBS.Registry.user.id ? CBS.Registry.user.id : 'anon';
        let key = this.brand + id + this.dai;

        return key;
    }

    // get item; assumes JSON.encoded item with expires value - returns object
    cacheGet(key) {
        if (!CACHE_ENABLED) {
            return null;
        }
        var item = null;
        if (key) {
            this.debug('Checking cache with key:', key);
            item = localStorage.getItem(key);
            if (item) {
                // check expiration derived from API response TTL
                item = JSON.parse(item);
                if (item.expires <= this.now()) {
                    this.debug('Cached data expired:', key);
                    this.cacheClear(key);
                    item = null;
                }
            }
        }

        return item;
    }

    // set item; JSON encodes item/object, assumes TTL in seconds
    cacheSet(item, key, ttl) {
        if (!CACHE_ENABLED) {
            return;
        }
        if (item && key) {
            this.debug('Caching with key:', key);
            if (ttl) {
                item.expires = this.now() + ttl * 1000;
            }
            localStorage.setItem(key, JSON.stringify(item));
        }
    }

    cacheClear(key) {
        if (!CACHE_ENABLED) {
            return;
        }
        this.debug('Clearing from cache:', key);
        localStorage.removeItem(key);
    }

    getUserIDKey() {
        const id = CBS.Registry.user.id ? CBS.Registry.user.id : 'anon';
        const key = `${this.brand}_fms_user_${id}`;
        return key;
    }

    getUserID() {
        const storage_key = this.getUserIDKey();
        let userID = localStorage.getItem(storage_key);
        if (!userID) {
            if (typeof this.uuidHandler?.v4 === 'function') {
                userID = this.uuidHandler.v4();
                localStorage.setItem(storage_key, userID);
            } else {
                userID = '';
            }
        }
        return userID;
    }

    // convenience: now as Unix timestamp in seconds
    now() {
        return new Date().getTime();
    }

    configureDebug(name, paramName) {
        this.debugConfig = {};
        this.debugConfig.name = name;
        this.debugConfig.paramName = paramName;

        var value;

        // debug query param ?debugFMS=1
        let urlParams = new URLSearchParams(window.location.search);
        value = urlParams.get(this.debugConfig.paramName);
        if (value !== null) {
            if (value == 1 || value == 'true' || value == 'yes') {
                value = true;
                setCookie(this.debugConfig.paramName, 1);
            } else if (value == 0 || value == 'false' || value == 'no') {
                value = false;
                removeCookie(this.debugConfig.paramName);
            }
        } else {
            // cookie value
            value = getCookie(this.debugConfig.paramName);
            if (value !== null) {
                if (value == 1 || value == 'true' || value == 'yes') {
                    value = true;
                }
            }
        }

        this.debugConfig.enabled = value;
    }

    // debug convenience
    debug(text, param = null) {
        if (this.debugConfig.enabled) {
            let string = this.debugConfig.name + ': ' + text;
            param ? console.log(string, param) : console.log(string);
        }
    }
}
